import { computed, ref, onMounted, onUnmounted, ComponentPublicInstance } from 'vue';
import { useRouter } from 'vue-router';
import { useDashboardPopupEnvStore } from '@/common/stores/dashboard-popup';
import { storeToRefs } from 'pinia';
import { DetailInfoData } from '@/common/utils/types';
import { useMyInfoStore } from '@/config/stores/my-info';
import { getTzTimeNow } from '../utils/time.utils';
import { openSafeUrl } from '../utils/browser/navigation';

export interface DashboardKey {
  target: string;
  targetId: string;
  namespace?: string;
}

export const useMoveDashboard = () => {
  const router = useRouter();

  const getTagString = (keyInfo?: DashboardKey) =>
    keyInfo?.namespace
      ? `${keyInfo?.target}+${keyInfo?.targetId}+${keyInfo?.namespace}`
      : `${keyInfo?.target}+${keyInfo?.targetId}`;

  const moveDashboardByMultiTarget = (
    id: number,
    { target, targetIds }: { target: string; targetIds: string[] },
  ) => {
    router.push({
      name: 'dashboard_Dashboard View',
      params: {
        id,
      },
      query: {
        rootMenu: 'dashboard',
        tag: targetIds.map((value) =>
          getTagString({
            target,
            targetId: value,
          }),
        ),
      },
    });
  };

  const moveDashboardByTarget = (id: number, keyInfo?: DashboardKey) => {
    router.push({
      name: 'dashboard_Dashboard View',
      params: {
        id,
      },
      query: {
        rootMenu: 'dashboard',
        tag: getTagString(keyInfo),
      },
    });
  };

  return {
    moveDashboardByMultiTarget,
    moveDashboardByTarget,
  };
};

export const useDashboardJump = (envKey: string) => {
  const isOpenDashboardSettingPopup = ref(false);
  const isOpenDashboardListPopup = ref(false);

  const { setupDashBoardData } = storeToRefs(useDashboardPopupEnvStore());

  const { moveDashboardByTarget: moveDashboard } = useMoveDashboard();

  const dashboardPopupList = computed(
    () => setupDashBoardData.value?.get(envKey)?.dashboardList ?? [],
  );
  const dashboardPopupListCnt = computed(() => dashboardPopupList.value.length ?? 0);

  const dashboardName = computed(() => setupDashBoardData.value?.get(envKey)?.name ?? '');

  const viewDashboardList = (
    event: Event,
    infoDataList: DetailInfoData[],
    keyInfo: DashboardKey,
  ) => {
    event.stopPropagation();
    const popupList = dashboardPopupList.value;
    const popupCount = dashboardPopupListCnt.value;
    const { myInfo: accountInfo } = useMyInfoStore();
    const containerKey = infoDataList?.filter((data) => data.field === 'containerKey')[0]
      ?.values[0];

    if (popupCount > 1) {
      isOpenDashboardListPopup.value = !isOpenDashboardListPopup.value;
      if (isOpenDashboardSettingPopup.value) {
        isOpenDashboardSettingPopup.value = false;
      }
    } else if (popupCount === 1 && popupList?.[0]) {
      const popup = popupList[0];
      isOpenDashboardListPopup.value = false;

      if (popup.dashboardId) {
        moveDashboard(popup.dashboardId, keyInfo);
      } else if (popup.url) {
        const today = getTzTimeNow();
        const fromTime = +today.subtract(10, 'minutes');
        const toTime = +today;
        const url = popup.url
          .replace('$intermaxFromTime', fromTime.toString())
          .replace('$intermaxToTime', toTime.toString())
          .replace('$timeZone', accountInfo.timezone ?? '')
          .replace('$containerId', containerKey ?? '');

        openSafeUrl(url, { newWindow: popup.isNewWindow });
      }
    }
  };

  const dashboardTooltipRef = ref();
  const onShowDashboardTooltip = (e) => {
    if (dashboardPopupListCnt.value === 0) {
      dashboardTooltipRef.value.show(e);
    }
  };
  const onHideDashboardTooltip = (e) => {
    const { relatedTarget } = e;
    if (!relatedTarget?.classList.contains('dashboard-msg-tooltip')) {
      dashboardTooltipRef.value.hide();
    }
  };

  const triggerButtonRef = ref<ComponentPublicInstance | null>(null);
  const popupAreaRef = ref<ComponentPublicInstance | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const isTriggerButton = triggerButtonRef.value?.$el?.contains(target);
    const isPopupArea = popupAreaRef.value?.$el?.contains(target);

    if (!isTriggerButton && !isPopupArea && isOpenDashboardListPopup.value) {
      isOpenDashboardListPopup.value = false;
    }
  };

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });

  return {
    isOpenDashboardSettingPopup,
    isOpenDashboardListPopup,
    dashboardPopupListCnt,
    dashboardPopupList,
    dashboardName,
    moveDashboard,
    viewDashboardList,

    dashboardTooltipRef,
    onShowDashboardTooltip,
    onHideDashboardTooltip,
    triggerButtonRef,
    popupAreaRef,
  };
};
